import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Flag, ShieldOff } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const REASONS = [
  { value: "inappropriate", label: "محتوى غير لائق" },
  { value: "spam", label: "سبام / إعلانات مزعجة" },
  { value: "scam", label: "احتيال أو خداع" },
  { value: "hate", label: "خطاب كراهية" },
  { value: "violence", label: "عنف أو تهديد" },
  { value: "copyright", label: "انتهاك حقوق ملكية" },
  { value: "wrong_info", label: "معلومات خاطئة" },
  { value: "other", label: "سبب آخر" },
];

/**
 * Reusable content report button + dialog.
 * Required by Apple App Store Guideline 1.2 for user-generated content.
 * Props:
 *   contentType: "shop" | "rating" | "story" | "post" | "promotion" | "product" | "user"
 *   contentId: string
 *   ownerUserId?: string  — optional: allows blocking the owner in the same dialog
 *   size?: "sm" | "default"
 *   variant?: "ghost" | "outline"
 *   label?: string
 */
export default function ReportButton({
  contentType,
  contentId,
  ownerUserId = null,
  size = "sm",
  variant = "ghost",
  label = "إبلاغ",
}) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [blockingUser, setBlockingUser] = useState(false);

  if (!user) return null; // hide for anon (Apple requires signed-in reporting)

  const submitReport = async () => {
    if (!reason) { toast.error("اختر سبب البلاغ"); return; }
    setBusy(true);
    try {
      await api.post("/reports", { content_type: contentType, content_id: contentId, reason, description });
      toast.success("تم إرسال البلاغ — سيراجعه فريق الإدارة خلال 24 ساعة");
      setOpen(false);
      setReason(""); setDescription("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "تعذّر إرسال البلاغ");
    }
    setBusy(false);
  };

  const blockOwner = async () => {
    if (!ownerUserId) return;
    if (ownerUserId === user.id) { toast.error("لا يمكنك حظر نفسك"); return; }
    setBlockingUser(true);
    try {
      await api.post("/blocks", { blocked_user_id: ownerUserId });
      toast.success("تم حظر هذا المستخدم — لن ترى محتواه بعد الآن");
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "تعذّر الحظر");
    }
    setBlockingUser(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant={variant}
          size={size}
          className="text-muted-foreground hover:text-rose-600 gap-1.5"
          data-testid={`report-btn-${contentType}-${contentId}`}
        >
          <Flag className="size-4" />
          <span className="text-xs">{label}</span>
        </Button>
      </DialogTrigger>
      <DialogContent dir="rtl" className="max-w-md" data-testid="report-dialog">
        <DialogTitle className="flex items-center gap-2">
          <Flag className="size-5 text-rose-600" /> إبلاغ عن محتوى
        </DialogTitle>
        <DialogDescription>
          استخدم هذه النافذة للإبلاغ عن محتوى مسيء أو غير لائق. سيراجع فريق الإدارة بلاغك خلال 24 ساعة.
        </DialogDescription>
        <div className="space-y-3 text-sm">
          <p className="text-muted-foreground">
            نأخذ البلاغات على محمل الجد. سيتم مراجعة هذا البلاغ خلال 24 ساعة والتصرف حسب سياسة استخدامنا.
          </p>
          <div>
            <label className="text-xs font-bold block mb-1">سبب البلاغ *</label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger data-testid="report-reason-select">
                <SelectValue placeholder="اختر السبب..." />
              </SelectTrigger>
              <SelectContent>
                {REASONS.map(r => (
                  <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-bold block mb-1">تفاصيل إضافية (اختياري)</label>
            <Textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={3}
              placeholder="اشرح المشكلة بمزيد من التفاصيل..."
              maxLength={500}
              data-testid="report-description"
            />
          </div>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={() => setOpen(false)} className="flex-1">إلغاء</Button>
            <Button
              onClick={submitReport}
              disabled={busy || !reason}
              className="flex-1 bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="report-submit-btn"
            >
              {busy ? "جاري الإرسال..." : "إرسال البلاغ"}
            </Button>
          </div>
          {ownerUserId && ownerUserId !== user.id && (
            <div className="pt-3 border-t">
              <Button
                variant="outline"
                onClick={blockOwner}
                disabled={blockingUser}
                className="w-full gap-2 text-rose-700 hover:bg-rose-50 border-rose-300"
                data-testid="block-user-btn"
              >
                <ShieldOff className="size-4" />
                {blockingUser ? "جاري الحظر..." : "حظر هذا المستخدم من محتواه"}
              </Button>
              <p className="text-[11px] text-muted-foreground mt-1.5 text-center">
                يمكنك إلغاء الحظر لاحقاً من صفحة حسابي
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
